import apiClient from '../../services/apiClient';

// Thin wrappers over /api/v1/learner-plan (BP 25). DC Connect (D2L) stays the
// system of record for every assessed instrument — this API only ever reads
// or writes reference records, class-time anchors, and status OBSERVATIONS
// (an import row or an instructor mark-off). Nothing here submits, marks, or
// grades anything.

const BASE = '/api/v1/learner-plan';

export type ItemType = 'quiz' | 'learning_activity' | 'milestone' | 'culminating_task' | 'assignment' | 'test';
export type StatusValue =
  | 'not_started' | 'in_progress' | 'submitted' | 'awaiting_results'
  | 'passed' | 'needs_retry' | 'graded';
export type StatusSource = 'd2l_import' | 'instructor';

export interface SessionBriefDto {
  id: string;
  seq: number;
  label: string;
  scheduled_date: string | null;
}

export interface StatusDto {
  status: StatusValue;
  score: number | null;
  max_score: number | null;
  source: StatusSource;
  observed_at: string;
}

export interface PolicyDto {
  attempts?: number;
  time_limit_min?: number;
  retake?: string;
  required?: boolean;
  pass_percent?: number;
  release_conditions?: string | null;
  note?: string;
  rubric_status?: string;
  reminder_seqs?: number[];
  [key: string]: unknown;
}

export interface AssessmentItemDto {
  key: string;
  title: string;
  item_type: ItemType;
  points: number | null;
  group_keys: string[];
  d2l_url: string | null;
  policy: PolicyDto;
  intro_session: SessionBriefDto | null;
  worktime_sessions: SessionBriefDto[];
  reminder_session: SessionBriefDto | null;
  due_at: SessionBriefDto | null;
  support: Record<string, unknown>;
  my_status: StatusDto | null;
}

export interface AssessmentGroupDto {
  key: string;
  title: string;
  opens_at: SessionBriefDto | null;
  due_at: SessionBriefDto | null;
}

export interface LearnerPlanDto {
  groups: AssessmentGroupDto[];
  items: AssessmentItemDto[];
}

export interface MyStatusRowDto extends StatusDto {
  item_key: string | null;
}

export interface M36Section {
  title: string;
  requirements: string[];
}
export interface M36Support {
  overview?: string;
  sections?: M36Section[];
  display_options?: string[];
  reflection_questions?: string[];
}

// ── Student ──────────────────────────────────────────────────────────────

export async function getPlan(): Promise<LearnerPlanDto> {
  const { data } = await apiClient.get<LearnerPlanDto>(`${BASE}/`);
  return data;
}

export async function getMyStatuses(): Promise<MyStatusRowDto[]> {
  const { data } = await apiClient.get<{ statuses: MyStatusRowDto[] }>(`${BASE}/me`);
  return data.statuses ?? [];
}

// ── Instructor ───────────────────────────────────────────────────────────

export interface SeedDefaultsResult {
  groups: { created: number; updated: number };
  items: { created: number; updated: number };
  warnings: string[];
}

export async function seedDefaults(): Promise<SeedDefaultsResult> {
  const { data } = await apiClient.post<SeedDefaultsResult>(`${BASE}/seed-defaults`);
  return data;
}

export async function updateGroup(
  key: string,
  patch: Partial<Pick<AssessmentGroupDto, 'title'>> & { opens_session_id?: string | null; due_session_id?: string | null },
): Promise<AssessmentGroupDto> {
  const { data } = await apiClient.put<{ group: AssessmentGroupDto }>(`${BASE}/groups/${key}`, patch);
  return data.group;
}

export async function updateItem(key: string, patch: Record<string, unknown>): Promise<AssessmentItemDto> {
  const { data } = await apiClient.put<{ item: AssessmentItemDto }>(`${BASE}/items/${key}`, patch);
  return data.item;
}

export interface BlockerDto {
  item_key: string;
  gates_item_key: string;
  students: Array<{ student_id: string | null; name: string | null; status: StatusValue; observed_at: string }>;
}

export interface SessionPlanDto {
  introduce: Array<Pick<AssessmentItemDto, 'key' | 'title' | 'item_type' | 'points' | 'd2l_url' | 'policy'>>;
  worktime: Array<Pick<AssessmentItemDto, 'key' | 'title' | 'item_type' | 'points' | 'd2l_url' | 'policy'>>;
  remind: Array<Pick<AssessmentItemDto, 'key' | 'title' | 'item_type' | 'points' | 'd2l_url' | 'policy'>>;
  due: Array<Pick<AssessmentItemDto, 'key' | 'title' | 'item_type' | 'points' | 'd2l_url' | 'policy'>>;
  blockers: BlockerDto[];
}

export async function getSessionPlan(classSessionId: string): Promise<SessionPlanDto> {
  const { data } = await apiClient.get<SessionPlanDto>(`${BASE}/sessions/${classSessionId}/plan`);
  return data;
}

export async function recordStatus(
  itemKey: string,
  body: { user_id: string; status: StatusValue; score?: number; max_score?: number; note?: string },
): Promise<StatusDto> {
  const { data } = await apiClient.post<{ status: StatusDto }>(`${BASE}/items/${itemKey}/status`, body);
  return data.status;
}

export interface ItemStatusRowDto {
  user: { id: string; name: string | null; student_id: string | null } | null;
  latest: StatusDto | null;
}

export async function getItemStatusRows(itemKey: string): Promise<ItemStatusRowDto[]> {
  const { data } = await apiClient.get<{ rows: ItemStatusRowDto[] }>(`${BASE}/items/${itemKey}/status`);
  return data.rows ?? [];
}

/** What one past import actually did, cell by cell (migration 022). Empty on
 * rows written before that — which reads as "this import predates the
 * counts", NOT as "it did nothing", and the UI has to say so. */
export interface D2lImportOutcome {
  created?: number;
  changed?: number;
  unchanged?: number;
  blank_skipped?: number;
  rejected_formula?: number;
  rejected_text?: number;
  overrode_instructor?: number;
  duplicate_ids?: Array<{ student_id: string; name: string | null }>;
  non_student_rows?: number;
}

export interface D2lImportDto {
  id: string;
  filename: string | null;
  exported_at: string;
  row_count: number;
  matched_students: number;
  unmatched_students: Array<{ student_id: string; name: string | null }>;
  matched_columns: Array<{ header: string; item_key: string; grade_item: string }>;
  ignored_columns: string[];
  outcome: D2lImportOutcome;
  created_at: string;
}

export interface ImportGradesResult {
  import: D2lImportDto;
  statuses_written: number;
  unmatched_students: Array<{ student_id: string; name: string | null }>;
  ignored_columns: string[];
  /** The same plan the dry run returns, for the file that was just applied. */
  plan?: ImportPlanDto;
}

// ── Import dry run ───────────────────────────────────────────────────────
// The preview and the apply are the SAME analysis on the backend
// (services/D2lGradesImportService.js §"the five safety rules"), which is what
// lets the UI promise "this is exactly what will change". Do NOT add a client
// path that applies a stored preview — the apply re-reads the file.

export type ImportAction =
  | 'create' | 'change' | 'blank_skipped' | 'rejected_formula' | 'rejected_text';

export interface ImportChangeValue {
  status: StatusValue;
  score: number | null;
  max_score: number | null;
}

export interface ImportChangeDto {
  student_id: string;
  name: string | null;
  user_id: string;
  item_key: string;
  item_title: string;
  column: string;
  cell: string;
  action: ImportAction;
  from: (ImportChangeValue & { source: StatusSource; observed_at: string }) | null;
  to: ImportChangeValue | null;
  reason?: string;
  overrides_instructor?: boolean;
}

export interface ImportPlanDto {
  dry_run?: boolean;
  filename?: string | null;
  columns: {
    matched: Array<{ header: string; item_key: string; grade_item: string }>;
    ignored: string[];
  };
  students: {
    matched: number;
    rows_in_file: number;
    non_student_rows: number;
    unmatched: Array<{ student_id: string; name: string | null }>;
    duplicate_ids: Array<{ student_id: string; name: string | null }>;
  };
  counts: {
    create: number;
    change: number;
    unchanged: number;
    blank_skipped: number;
    rejected_formula: number;
    rejected_text: number;
    will_write: number;
  };
  changes: ImportChangeDto[];
  exported_at: string;
}

/** DRY RUN — writes nothing at all. 200, not 201: nothing was created. */
export async function previewImport(file: File, exportedAt: string): Promise<ImportPlanDto> {
  const form = new FormData();
  form.append('file', file);
  form.append('exported_at', exportedAt);
  const { data } = await apiClient.post<ImportPlanDto>(`${BASE}/imports/preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function importGrades(file: File, exportedAt: string): Promise<ImportGradesResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('exported_at', exportedAt);
  const { data } = await apiClient.post<ImportGradesResult>(`${BASE}/imports`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ── Progress report ──────────────────────────────────────────────────────

export type ItemReportState =
  | 'complete' | 'awaiting_result' | 'needs_retry' | 'in_progress' | 'not_started' | 'no_information';

export interface ReportItemDto {
  key: string;
  title: string;
  item_type: ItemType;
  points: number | null;
  group_keys: string[];
  due: { seq: number; label: string; date: string | null; from: string | null } | null;
  past_due: boolean;
  state: ItemReportState;
  state_label: string;
  status: StatusValue | null;
  score: number | null;
  max_score: number | null;
  percent: number | null;
  source: StatusSource | null;
  observed_at: string | null;
}

export interface ReportAttendanceSession {
  seq: number;
  label: string;
  date: string | null;
  cell: string;
  quality: string | null;
  flags: string[];
}

export interface ProgressReportDto {
  generated_at: string;
  course: { id: string; code: string | null; title: string };
  term: {
    first_meeting: string | null;
    last_meeting: string | null;
    meetings_total: number;
    meetings_dated: number;
    meetings_to_date: number;
    as_of: string;
  };
  student: {
    id: string;
    student_id: string | null;
    name: string;
    email: string | null;
    learner_status: string;
  };
  plan: {
    items: ReportItemDto[];
    counts: {
      total: number;
      complete: number;
      awaiting_result: number;
      needs_retry: number;
      in_progress: number;
      not_started: number;
      no_information: number;
      overdue: number;
      past_due_no_information: number;
    };
    points: {
      earned: number;
      out_of: number;
      percent: number | null;
      items_counted: number;
      items_total: number;
    };
  };
  effort: {
    lessons: { completed: number; total: number; percent: number | null; xp: number };
    quiz_checks: { attempts: number; correct: number; accuracy_percent: number };
    participation: { responded: number; expected: number; percent: number | null };
    attendance: {
      present: number;
      eligible: number;
      percent: number;
      by_method: { code: number; manual: number; remote_exception: number };
      voided: number;
      flagged: number;
      meetings_without_window: number;
      sessions: ReportAttendanceSession[];
    };
    engagement: { logins: number; devices: number; last_seen_at: string | null };
  };
  trend: {
    available: boolean;
    reason?: string;
    points: Array<{ item_key: string; item_title: string; at: string; percent: number }>;
  };
  summary: string[];
  caveats: string[];
  sources: { first_hand: string[]; mirrored: string[] };
}

export async function getMyProgressReport(): Promise<ProgressReportDto> {
  const { data } = await apiClient.get<ProgressReportDto>(`${BASE}/report/me`);
  return data;
}

export async function getStudentProgressReport(userId: string): Promise<ProgressReportDto> {
  const { data } = await apiClient.get<ProgressReportDto>(`${BASE}/students/${userId}/report`);
  return data;
}

export async function listImports(): Promise<D2lImportDto[]> {
  const { data } = await apiClient.get<{ imports: D2lImportDto[] }>(`${BASE}/imports`);
  return data.imports ?? [];
}

export function reportCsvUrl(): string {
  return `${BASE}/report.csv`;
}

// ── Issued progress reports ──────────────────────────────────────────────
//
// An issued report is a SNAPSHOT taken when the instructor issued it, not a
// live view — see backend/src/services/ProgressReportIssueService.js for why.
// `report` below is therefore the figures as they stood at `issued_at`, which
// is why every surface that renders one must show that date.

export interface IssuedReportDto {
  id: string;
  user_id: string;
  issued_at: string;
  issued_by: string;
  batch_id: string | null;
  content_hash: string;
  report: ProgressReportDto;
}

export interface IssueRecordDto {
  id: string;
  user_id: string;
  issued_at: string;
  issued_by: string;
  batch_id: string | null;
  content_hash: string;
}

export type IssueOutcome = 'issued' | 'unchanged' | 'failed';

export interface IssueResultDto {
  outcome: IssueOutcome;
  issue: IssuedReportDto;
}

export interface IssueAllRowDto {
  user_id: string;
  student_id: string | null;
  outcome: IssueOutcome;
  issue_id?: string | null;
  issued_at?: string | null;
  reason?: string;
}

export interface IssueAllResultDto {
  batch_id: string;
  counts: { total: number; issued: number; unchanged: number; failed: number };
  results: IssueAllRowDto[];
}

/** The caller's own latest issued report, or null when nothing has been
 * issued to them. The backend answers 204 for that case rather than 404 —
 * "your instructor has not issued one yet" is a normal state, not an error. */
export async function getMyIssuedReport(): Promise<IssuedReportDto | null> {
  const res = await apiClient.get<IssuedReportDto | ''>(`${BASE}/report/issued/me`);
  if (res.status === 204 || !res.data) return null;
  return res.data as IssuedReportDto;
}

/** Every report issued to the caller, newest first — so a student can reopen
 * the one that was actually discussed with them, not only the newest. */
export async function getMyIssuedReportHistory(): Promise<IssueRecordDto[]> {
  const { data } = await apiClient.get<{ issues: IssueRecordDto[] }>(`${BASE}/report/issued/me/history`);
  return data.issues ?? [];
}

/** One of the caller's OWN issued reports. The backend scopes the lookup by
 * the caller's id, so someone else's id is an ordinary 404. */
export async function getMyIssuedReportById(issueId: string): Promise<IssuedReportDto> {
  const { data } = await apiClient.get<IssuedReportDto>(`${BASE}/report/issued/me/${issueId}`);
  return data;
}

export async function issueReportForStudent(userId: string): Promise<IssueResultDto> {
  const { data } = await apiClient.post<IssueResultDto>(`${BASE}/students/${userId}/report/issue`);
  return data;
}

export async function listIssuesForStudent(userId: string): Promise<IssueRecordDto[]> {
  const { data } = await apiClient.get<{ issues: IssueRecordDto[] }>(`${BASE}/students/${userId}/report/issues`);
  return data.issues ?? [];
}

export async function issueReportForAll(): Promise<IssueAllResultDto> {
  const { data } = await apiClient.post<IssueAllResultDto>(`${BASE}/report/issue-all`);
  return data;
}
